import { action, makeObservable, observable, toJS } from 'mobx';

import {
  MonetizationEventsEventType,
  type SportMonetizationCurrentEvent,
} from '../../../dto/monetization-events.types';
import { postRequest } from '../../../services/api';
import { getUserStats } from '../../../services/launchDarkly';
import type { ApiResponse } from '../../../services/types';
import { getErrorMessage } from '../../../utils/errors';
import { devError } from '../../../utils/log';
import type { FeatureManagerDialogType } from '../../feature-manager/types';
import type { LaunchGameData } from '../../game/types';
import type { RootStore } from '../../RootStore';
import { EnumOpenDialog } from '../../ui/types';

import { SPORT_BASE_PATH, SPORT_EVENTS_PATHS, SPORTS_DIALOG_TYPES } from './constants';

export class SportEventsStore {
  private readonly rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeObservable(this);
  }

  @observable
  public isLoadingGame = false;

  @action
  public setIsLoadingGame(loading: boolean) {
    this.isLoadingGame = loading;
  }

  @observable
  public sportEvent: SportMonetizationCurrentEvent | undefined = undefined;

  @action
  public setSportEvent(event: SportMonetizationCurrentEvent | undefined) {
    this.sportEvent = event;
  }

  @observable
  public currentOpenSportEvent: SportMonetizationCurrentEvent | undefined = undefined;

  @action
  public setCurrentOpenSportEvent(event: SportMonetizationCurrentEvent | undefined) {
    this.currentOpenSportEvent = event;
    this.rootStore.monetizationEventsStore.setCurrentOpenSportEvent(event);
  }

  public iframeRef: HTMLIFrameElement | null = null;

  public setIframeRef = (iframe: HTMLIFrameElement | null) => {
    this.iframeRef = iframe;
  };

  public closeGame() {
    this.setCurrentOpenSportEvent(undefined);
    this.rootStore.monetizationEventsStore.onMonetizationEventsDialogClose?.();
    this.rootStore.uiStore.setCloseDialog();

    const dialogType = this.sportEvent ? SPORTS_DIALOG_TYPES[this.sportEvent.gameNameType] : undefined;

    if (dialogType) {
      this.rootStore.featureManagerStore.onDialogFinished(dialogType);
    }
  }

  public async startSportEvent() {
    try {
      let sportEventUrl: string | undefined;

      if (!this.sportEvent) {
        devError('SportEventsStore: startSportEvent error: sport Event not found');
        return;
      }

      const launchGameResponse = await postRequest<ApiResponse<LaunchGameData>>('user/launch-game', {
        coin: 'ALL',
        gameId: this.sportEvent?.gameId,
      });

      if (!launchGameResponse.data?.data) {
        devError('SportEventsStore: startSportEvent error: launchGameResponse not found');
        return;
      }

      const { sportsEvents } = launchGameResponse.data.data;

      if (sportsEvents) {
        const { token } = sportsEvents;
        const { gameUiFlag } = this.rootStore.gameStore;

        let path = '';

        if (Object.values(SPORT_EVENTS_PATHS).includes(sportsEvents.path)) {
          path = `${SPORT_BASE_PATH}/${sportsEvents.path}`;
        } else {
          devError('SportEventsStore: startSportEvent error: invalid path');
          return;
        }

        sportEventUrl = this.rootStore.gameUiStore.getGameUiURL(path, {
          monetizationEventsToken: gameUiFlag ? token : undefined,
          token: gameUiFlag ? undefined : token,
          coinType: 'ALL',
          eventType: MonetizationEventsEventType.SPORTS,
        });

        const sportEventToSet = {
          ...this.sportEvent,
          token,
          path,
          url: sportEventUrl,
          startDate: this.sportEvent.startDate,
          endDate: this.sportEvent.endDate,
        };

        this.setCurrentOpenSportEvent(sportEventToSet);

        this.rootStore.uiStore.setOpenDialog({
          dialog: EnumOpenDialog.isOpenMonetizationEventsDialog,
          options: { type: MonetizationEventsEventType.SPORTS },
        });
      } else {
        devError('SportEventsStore: startSportEvent error: sport event not found');
        this.closeGame();
      }
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      devError('SportEventsStore: startSportEvent error', errorMessage);
    } finally {
      this.setIsLoadingGame(false);
    }
  }

  sendSportEventGameUiMessage({ type, payload }: { type: string; payload: any }) {
    if (!this.iframeRef || !this.iframeRef.contentWindow) {
      devError('SportEventsStore sendSportEventGameUiMessage: Iframe content window is not available');
      return;
    }

    this.iframeRef.contentWindow.postMessage(
      {
        type,
        payload,
      },
      import.meta.env.VITE_GAME_UI_URL,
    );
  }

  public handleSportEventIframeLoad(iframeRef: HTMLIFrameElement, gameType: string) {
    if (!iframeRef.contentWindow) {
      devError('SportEventsStore handleSportEventIframeLoad: Iframe content window is not available');
      return;
    }

    this.setIframeRef(iframeRef);

    if (this.currentOpenSportEvent) {
      this.sendSportEventGameUiMessage({
        type: 'newSportEvent',
        payload: {
          sportEvent: JSON.parse(JSON.stringify(toJS(this.currentOpenSportEvent))),
          gameType,
        },
      });

      if (this.rootStore.userStore.profile) {
        this.sendSportEventGameUiMessage({
          type: 'userReady',
          payload: {
            userProfile: getUserStats(this.rootStore.userStore.profile?.stats),
            language: this.rootStore.userStore.profile.language,
          },
        });
      }
    }
  }

  public startDialogs(type: FeatureManagerDialogType): Promise<boolean> | boolean {
    if (!this.sportEvent || !this.sportEvent.displayPopup) return false;

    if (type === SPORTS_DIALOG_TYPES[this.sportEvent.gameNameType]) {
      void this.startSportEvent();
      return true;
    }

    return false;
  }

  public sendAnalyticsEvent(analyticsParams: any) {
    this.rootStore.monetizationEventsStore.sendAnalyticsEvent({
      ...analyticsParams,
      feature_sub_type: this.currentOpenSportEvent?.gameNameType,
    });
  }
}
